/**
 * Computes a deterministic 32-bit hash for a string.
 *
 * @remarks
 * This is used as a stable seed to derive colors from arbitrary strings (e.g. usernames, ids).
 * The result may be negative due to 32-bit overflow semantics in JavaScript bitwise operations.
 *
 * @param value - Input string to hash. `null`/`undefined`/empty strings produce `0`.
 * @returns A signed 32-bit integer hash.
 *
 * @internal
 */
const getHashCode = (value?: string | null): number => {
  let hash = 0

  if (value?.length && value.length !== 0) {
    for (let i = 0; i < value.length; i++) {
      hash = value.charCodeAt(i) + ((hash << 5) - hash)
      hash = hash & hash
    }
  }

  return hash
}

/**
 * Converts an arbitrary string into a deterministic `rgb(r, g, b)` CSS color.
 *
 * @remarks
 * Useful for generating consistent avatar/tag/chart colors from an identifier.
 * The produced values are derived from a 32-bit hash and are therefore deterministic:
 * the same input always yields the same output.
 *
 * @param value - Input string used as a seed. `null`/`undefined`/empty strings yield `rgb(0, 0, 0)`.
 * @returns A CSS `rgb(...)` color string.
 *
 * @example
 * ```ts
 * intoRGB('alice') // "rgb(64, 73, 159)" (example output)
 * ```
 */
export const intoRGB = (value?: string | null): string => {
  const hash = getHashCode?.(value)
  const rgb = [0, 0, 0]

  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 255
    rgb[i] = value
  }

  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
}

/**
 * Converts a 6-digit hex color into a CSS `rgba(r, g, b, a)` string.
 *
 * @remarks
 * Expects a hex string in the form `#RRGGBB` or `RRGGBB` (3-digit shorthand is not supported).
 * `opacity` is passed through as-is; callers should supply a value in the range `[0..1]`.
 *
 * @param hex - A 6-digit hex color (with or without leading `#`).
 * @param opacity - Alpha channel value (typically `0..1`).
 * @returns A CSS `rgba(...)` color string.
 *
 * @example
 * ```ts
 * hexToRgba('#ff0000', 0.2) // "rgba(255, 0, 0, 0.2)"
 * ```
 */
export const hexToRgba = (hex: string, opacity: number): string => {
  hex = hex?.replace(/^#/, '')
  const r = parseInt(hex?.substring(0, 2), 16)
  const g = parseInt(hex?.substring(2, 4), 16)
  const b = parseInt(hex?.substring(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * Chooses a readable text color (`black` or `white`) for a given background hex color.
 *
 * @remarks
 * Uses a simple luminance/brightness heuristic based on the RGB channels and a fixed threshold.
 * Expects a hex string in the form `#RRGGBB` or `RRGGBB`. If `hex` is missing, returns `black`.
 *
 * @param hex - Background hex color (with or without leading `#`).
 * @returns `'black'` for light backgrounds, `'white'` for dark backgrounds.
 *
 * @example
 * ```ts
 * getContrastTextColor('#ffffff') // "black"
 * getContrastTextColor('#000000') // "white"
 * ```
 */
export const getContrastTextColor = (
  hex?: string | null
): 'black' | 'white' => {
  if (!hex) return 'black'

  hex = hex?.replace(/^#/, '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  const brightness = (r * 299 + g * 587 + b * 114) / 1000

  return brightness > 186 ? 'black' : 'white'
}
